import {
  IRatioCalculationAsset,
  TSwapOutcome,
  calculateLiquidity,
  getSwapOutput,
  getSwapRatio,
} from "@sundaeswap/cpp";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDebounce } from "@sundae/react-hooks";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import divide from "lodash/divide";
import { INPUT_DEBOUNCE_IN_MS } from "../../../constants/page.constants";
import { useAdaBalance } from "../../../hooks/useAdaBalance";
import { useAssetsWithBalance } from "../../../hooks/useAssetsWithBalance";
import { IAssetMetaData } from "../../../types/Asset.types";
import { isSameAsset } from "../../../utils/assets.utils";
import { Actions, useAppContext } from "../../AppContext";
import { ELiquidityFlowState, useLiquidityContext } from "../context";

export interface IZapContext {
  derived: {
    zapAssetAmount?: AssetAmount<IAssetMetaData>;
    zapAssetAmountExceedsBalance?: boolean;
  };
  handlers: {
    handleCloseDialog: () => void;
    setAPerB: Dispatch<SetStateAction<string>>;
    setBPerA: Dispatch<SetStateAction<string>>;
    setLiquidity: Dispatch<SetStateAction<ReturnType<typeof calculateLiquidity>>>;
    setSwapOutcome: Dispatch<SetStateAction<TSwapOutcome>>;
    setZapInputValue: Dispatch<SetStateAction<string>>;
  };
  state: {
    aPerB?: string;
    bPerA?: string;
    debouncedInputValue?: string;
    debouncedSwapOutcome?: TSwapOutcome;
    liquidity?: ReturnType<typeof calculateLiquidity>;
    swapOutcome?: TSwapOutcome;
    zapInputValue: string;
  };
}

const defaultZapState: IZapContext = {
  derived: {
    zapAssetAmount: undefined,
  },
  handlers: {
    handleCloseDialog: () => {},
    setAPerB: () => {},
    setBPerA: () => {},
    setLiquidity: () => {},
    setSwapOutcome: () => {},
    setZapInputValue: () => {},
  },
  state: {
    aPerB: undefined,
    bPerA: undefined,
    liquidity: undefined,
    swapOutcome: undefined,
    zapInputValue: "",
  },
};

const ZapContext = createContext(defaultZapState);

export const useZapContext = () => useContext(ZapContext);

const ZapContextProvider: FC<PropsWithChildren> = ({ children }) => {
  /**
   * Local State
   */
  const [zapInputValue, setZapInputValue] = useState("");
  const [aPerB, setAPerB] = useState("");
  const [bPerA, setBPerA] = useState("");
  const [swapOutcome, setSwapOutcome] = useState<TSwapOutcome>();
  const [liquidity, setLiquidity] = useState<ReturnType<typeof calculateLiquidity>>();
  const debouncedInputValue = useDebounce(zapInputValue, { wait: INPUT_DEBOUNCE_IN_MS });
  const debouncedSwapOutcome = useDebounce(swapOutcome, { wait: INPUT_DEBOUNCE_IN_MS });

  const {
    dispatch: appDispatch,
    state: { activeZapAsset, activeZapPool },
  } = useAppContext();
  const {
    handlers: { setLiquidityFlowState },
  } = useLiquidityContext();
  const { assetsWithBalance } = useAssetsWithBalance();
  const { availableAdaBalance } = useAdaBalance();

  /**
   * Resets the context states
   */
  const handleZapContextReset = useCallback(() => {
    setSwapOutcome(undefined);
    setAPerB("");
    setBPerA("");
    setLiquidity(undefined);
  }, []);

  /**
   * Converts the input value to an AssetAmount.
   */
  const zapAssetAmount = useMemo(
    () => AssetAmount.fromValue(zapInputValue, activeZapAsset?.metadata),
    [zapInputValue, activeZapAsset?.metadata]
  );

  /**
   * Keeps track of whether the user has entered an amount that exceeds their balance.
   */
  const zapAssetAmountExceedsBalance = useMemo(() => {
    const isAssetAda = SundaeUtils.ADA_ASSET_IDS.includes(activeZapAsset?.metadata.assetId ?? "");

    if (isAssetAda && availableAdaBalance) {
      return zapAssetAmount?.amount > availableAdaBalance.quantity.amount;
    }

    const balance =
      assetsWithBalance?.find((asset) =>
        SundaeUtils.isAssetIdsEqual(asset.metadata.assetId, activeZapAsset?.metadata.assetId ?? "")
      )?.quantity.amount ?? 0n;

    return !!(zapAssetAmount?.amount > balance);
  }, [activeZapAsset, availableAdaBalance, assetsWithBalance, zapAssetAmount]);

  /**
   * Closes the dialog and resets the liquidity flow state.
   */
  const handleCloseDialog = useCallback(() => {
    setLiquidityFlowState(ELiquidityFlowState.initial);
    Actions.setActiveZapPool(undefined, appDispatch);
    Actions.setDialogState({ dialog: "liquidity.zap", isOpen: false }, appDispatch);
  }, [appDispatch]);

  useEffect(() => {
    if (!activeZapPool || !activeZapAsset || !zapAssetAmount) {
      return;
    }

    // Reset
    if (zapAssetAmount.amount === 0n) {
      handleZapContextReset();
      return;
    }

    const { assetA, quantityLP, quantityA, quantityB, assetB, fee } = activeZapPool;
    const input = zapAssetAmount.amount / 2n;

    // If the deposited amount is not divisible and ends up being 0, we ignore.
    if (input === 0n) {
      return;
    }
    const isZapAdaAsset = SundaeUtils.ADA_ASSET_IDS.includes(activeZapAsset.metadata.assetId);
    const isPoolAdaAsset = SundaeUtils.ADA_ASSET_IDS.includes(assetA.assetId);
    const isAdaAssetA =
      (isZapAdaAsset && isPoolAdaAsset) || isSameAsset(activeZapAsset.metadata, assetA);
    const inputReserve = isAdaAssetA ? quantityA : quantityB;
    const outputReserve = isAdaAssetA ? quantityB : quantityA;

    const swapOutcome = getSwapOutput(
      input,
      BigInt(inputReserve ?? "0"),
      BigInt(outputReserve ?? "0"),
      divide(Number(fee), 100) || "0"
    );

    let liquidity: ReturnType<typeof calculateLiquidity> | undefined;
    try {
      liquidity = calculateLiquidity(
        swapOutcome.input,
        swapOutcome.output,
        BigInt(swapOutcome.nextInputReserve),
        BigInt(swapOutcome.nextOutputReserve),
        BigInt(quantityLP ?? "0")
      );
    } catch (e) {
      // Ignore error about 0 as requiredB.
    }
    setLiquidity(liquidity);
    setSwapOutcome(swapOutcome);

    const poolReserveAssets: [IRatioCalculationAsset, IRatioCalculationAsset] = [
      {
        quantity: BigInt(quantityA ?? "0"),
        decimals: assetA.decimals,
        assetId: assetA.assetId,
      },
      {
        quantity: BigInt(quantityB ?? "0"),
        decimals: assetB.decimals,
        assetId: assetB.assetId,
      },
    ];

    const aPerB = getSwapRatio("A_PER_B", poolReserveAssets);
    const bPerA = getSwapRatio("B_PER_A", poolReserveAssets);

    setAPerB(aPerB.display);
    setBPerA(bPerA.display);
  }, [activeZapPool, activeZapAsset, zapInputValue, zapAssetAmount]);

  return (
    <ZapContext.Provider
      value={{
        derived: { zapAssetAmount, zapAssetAmountExceedsBalance },
        handlers: {
          handleCloseDialog,
          setAPerB,
          setBPerA,
          setLiquidity,
          setSwapOutcome,
          setZapInputValue,
        },
        state: {
          aPerB,
          bPerA,
          debouncedInputValue,
          debouncedSwapOutcome,
          liquidity,
          swapOutcome,
          zapInputValue,
        },
      }}
    >
      {children}
    </ZapContext.Provider>
  );
};

export default ZapContextProvider;
